import * as Cesium from "cesium";

export function createLaneletChainChecker(viewer, options = {}) {
  const { onStart, onComplete, getInterval, intervalMs, onStateChange } = options;

  const changedEntities = new Map(); // billboard changes
  const overlayEntities = new Map(); // overlay polylines
  let currentEntity = null;
  let timerId = null;
  let isRunning = false;
  let destroyed = false;
  let started = false;
  let completed = false;
  const handler = new Cesium.ScreenSpaceEventHandler(viewer.canvas);
  const mapClickDestroyHandler = new Cesium.ScreenSpaceEventHandler(viewer.canvas);
  let startClickPosition = null;

  const MAIN_JOIN_TOLERANCE_METERS = 0.01;
  const SIDE_JOIN_TOLERANCE_METERS = 0.001;

  // shared scratch objects to reduce allocations
  const scratchCarto1 = new Cesium.Cartographic();
  const scratchCarto2 = new Cesium.Cartographic();
  const scratchGeodesic = new Cesium.EllipsoidGeodesic();

  // entity cache for one dataSource
  let cachedDataSource = null;
  const entityCache = new Map(); // entity -> { positions, start, end, startDir, forwardDir }

  // global index for properties.id
  let propertiesIdIndex = null;

  // ===== Lifecycle helpers =====

  function emitState() {
    try {
      onStateChange?.({ isRunning, started, destroyed, completed });
    } catch {}
  }

  function callCompleteOnce() {
    if (completed) return;
    completed = true;
    try {
      if (typeof onComplete === "function") onComplete();
    } catch {}
    emitState();
  }

  function callStartOnce() {
    if (started) return;
    started = true;
    try {
      if (typeof onStart === "function") onStart();
    } catch {}
    emitState();
  }

  // ===== Utilities =====

  function resolveProp(prop) {
    try {
      if (prop == null) return undefined;
      if (typeof prop === "function") return prop();
      if (typeof prop.getValue === "function") return prop.getValue(Cesium.JulianDate.now());
      if (typeof prop.value !== "undefined") return prop.value;
      return prop;
    } catch (e) {
      return undefined;
    }
  }

  function getLayerName(entity, viewer) {
    if (!entity) return undefined;
    try {
      if (entity.properties && entity.properties.layerName) {
        const v = resolveProp(entity.properties.layerName);
        if (typeof v === "string") return v;
      }
    } catch (e) {}
    try {
      if (entity.dataSource && typeof entity.dataSource.layerName === "string") {
        try {
          entity._dataSource = entity.dataSource;
        } catch (e) {}
        return entity.dataSource.layerName;
      }
      if (entity._dataSource && typeof entity._dataSource.layerName === "string") {
        return entity._dataSource.layerName;
      }
    } catch (e) {}
    try {
      const ds = entity.entityCollection && entity.entityCollection.owner;
      if (ds && typeof ds.layerName === "string") {
        try {
          entity._dataSource = ds;
        } catch (e) {}
        return ds.layerName;
      }
      if (ds && typeof ds.name === "string") {
        try {
          entity._dataSource = ds;
        } catch (e) {}
        return ds.name;
      }
    } catch (e) {}
    try {
      if (viewer?.dataSources && typeof viewer.dataSources.get === "function") {
        for (let i = 0; i < viewer.dataSources.length; i++) {
          const ds = viewer.dataSources.get(i);
          if (!ds || !ds.entities) continue;
          try {
            if (typeof ds.entities.contains === "function" && ds.entities.contains(entity)) {
              try {
                entity._dataSource = ds;
              } catch (e) {}
              return typeof ds.layerName === "string" ? ds.layerName : typeof ds.name === "string" ? ds.name : undefined;
            }
          } catch (e) {
            const arr = ds.entities.values || ds.entities._entities || [];
            for (let j = 0; j < arr.length; j++) {
              const ej = arr[j];
              if (ej === entity || (ej?.id && entity.id && ej.id === entity.id)) {
                try {
                  entity._dataSource = ds;
                } catch (err) {}
                return typeof ds.layerName === "string" ? ds.layerName : typeof ds.name === "string" ? ds.name : undefined;
              }
            }
          }
        }
      }
    } catch (e) {}
    try {
      if (typeof entity.layerName === "string") return entity.layerName;
      if (typeof entity._layerName === "string") return entity._layerName;
    } catch (e) {}
    return undefined;
  }

  function getEntityDataSource(entity) {
    if (!entity) return null;
    if (entity._dataSource) return entity._dataSource;
    try {
      const owner = entity.entityCollection && entity.entityCollection.owner;
      if (owner) return owner;
    } catch (e) {}
    try {
      if (viewer?.dataSources && typeof viewer.dataSources.get === "function") {
        for (let i = 0; i < viewer.dataSources.length; i++) {
          const ds = viewer.dataSources.get(i);
          if (!ds || !ds.entities) continue;
          try {
            if (typeof ds.entities.contains === "function" && ds.entities.contains(entity)) {
              try {
                entity._dataSource = ds;
              } catch (e) {}
              return ds;
            }
          } catch (e) {
            const arr = ds.entities.values || ds.entities._entities || [];
            for (let j = 0; j < arr.length; j++) {
              const ej = arr[j];
              if (ej === entity || (ej?.id && entity.id && ej.id === entity.id)) {
                try {
                  entity._dataSource = ds;
                } catch (err) {}
                return ds;
              }
            }
          }
        }
      }
    } catch (e) {}
    return null;
  }

  // raw positions without cache to bootstrap cache
  function getPositionsArrayRaw(entity) {
    if (!entity) return null;
    if (entity.polyline?.positions) {
      const posProp = entity.polyline.positions;
      if (typeof posProp.getValue === "function") {
        const val = posProp.getValue(Cesium.JulianDate.now());
        if (Array.isArray(val) && val.length) return val;
        if (val?.positions && Array.isArray(val.positions)) return val.positions;
      } else if (Array.isArray(posProp)) return posProp;
      else if (posProp?.value && Array.isArray(posProp.value)) return posProp.value;
    }
    if (entity.polygon?.hierarchy) {
      const hier = entity.polygon.hierarchy;
      if (typeof hier.getValue === "function") {
        const val = hier.getValue(Cesium.JulianDate.now());
        if (Array.isArray(val)) return val;
        if (val?.positions && Array.isArray(val.positions)) return val.positions;
        if (val?.outer && Array.isArray(val.outer)) return val.outer;
      } else if (Array.isArray(hier)) return hier;
      else if (hier?.value && Array.isArray(hier.value)) return hier.value;
    }
    if (entity.polyline?.hierarchy) {
      const h = entity.polyline.hierarchy;
      if (typeof h.getValue === "function") {
        const val = h.getValue(Cesium.JulianDate.now());
        if (Array.isArray(val)) return val;
        if (val?.positions && Array.isArray(val.positions)) return val.positions;
      } else if (Array.isArray(h)) return h;
      else if (h?.value && Array.isArray(h.value)) return h.value;
    }
    return null;
  }

  // ensure cache for one dataSource around source entity
  function ensureEntityCacheFor(sourceEntity) {
    const ds = getEntityDataSource(sourceEntity);
    if (!ds || !ds.entities) return null;
    if (cachedDataSource === ds && entityCache.size > 0) return ds;

    entityCache.clear();
    cachedDataSource = ds;

    const arr = ds.entities.values || ds.entities._entities || [];
    for (let i = 0; i < arr.length; i++) {
      const e = arr[i];
      if (!e) continue;
      const pos = getPositionsArrayRaw(e);
      if (!pos || pos.length === 0) continue;

      const first = pos[0];
      const last = pos[pos.length - 1];

      let startDir = null;
      if (pos.length >= 2) {
        let i1 = 0;
        let i2 = 1;
        while (i2 < pos.length) {
          const v = Cesium.Cartesian3.subtract(pos[i2], pos[i1], new Cesium.Cartesian3());
          if (Cesium.Cartesian3.magnitude(v) > 0) {
            startDir = Cesium.Cartesian3.normalize(v, new Cesium.Cartesian3());
            break;
          }
          i1 = i2;
          i2 = i1 + 1;
        }
      }

      let forwardDir = null;
      if (pos.length >= 2) {
        let i2 = pos.length - 1;
        let i1 = i2 - 1;
        while (i1 >= 0) {
          const v = Cesium.Cartesian3.subtract(pos[i2], pos[i1], new Cesium.Cartesian3());
          if (Cesium.Cartesian3.magnitude(v) > 0) {
            forwardDir = Cesium.Cartesian3.normalize(v, new Cesium.Cartesian3());
            break;
          }
          i2 = i1;
          i1 = i2 - 1;
        }
      }

      entityCache.set(e, { positions: pos, start: first, end: last, startDir, forwardDir });
    }
    return ds;
  }

  // cached positions getter
  function getPositionsArray(entity) {
    const info = entityCache.get(entity);
    if (info && info.positions) return info.positions;
    return getPositionsArrayRaw(entity);
  }

  function getLineEndpoints(entity) {
    const info = entityCache.get(entity);
    if (info && info.start && info.end) {
      return { start: info.start, end: info.end };
    }
    const pos = getPositionsArray(entity);
    if (!pos || pos.length === 0) return null;
    if (pos.length === 1) return { start: pos[0], end: pos[0] };
    return { start: pos[0], end: pos[pos.length - 1] };
  }

  function distance2DMeters(a, b) {
    if (!a || !b) return Number.POSITIVE_INFINITY;
    const ca = Cesium.Cartographic.fromCartesian(a, Cesium.Ellipsoid.WGS84, scratchCarto1);
    const cb = Cesium.Cartographic.fromCartesian(b, Cesium.Ellipsoid.WGS84, scratchCarto2);
    if (!ca || !cb) return Number.POSITIVE_INFINITY;
    ca.height = 0;
    cb.height = 0;
    scratchGeodesic.setEndPoints(ca, cb);
    const d = scratchGeodesic.surfaceDistance;
    return typeof d === "number" && isFinite(d) ? d : Number.POSITIVE_INFINITY;
  }

  function cartesianNear2DMeters(a, b, tolMeters) {
    const tol = Math.max(0.0005, tolMeters || 0.001);
    const d = distance2DMeters(a, b);
    return d <= tol;
  }

  // ===== Visual helpers =====

  function makeOverlayPositions(positions, offset = 0.5) {
    if (!positions || !positions.length) return positions;
    const ellipsoid = Cesium.Ellipsoid.WGS84;
    const result = new Array(positions.length);
    for (let i = 0; i < positions.length; i++) {
      const carto = Cesium.Cartographic.fromCartesian(positions[i], ellipsoid);
      const h = (carto.height || 0) + offset;
      result[i] = Cesium.Cartesian3.fromRadians(carto.longitude, carto.latitude, h);
    }
    return result;
  }

  function setEntityColor(entity, color, width) {
    if (!entity) return;

    // polyline: draw overlay a bit above original
    if (entity.polyline) {
      const positions = getPositionsArray(entity);
      if (!positions || !positions.length) return;

      const elevatedPositions = makeOverlayPositions(positions);

      let record = overlayEntities.get(entity);
      const ds = getEntityDataSource(entity);
      const collection = (ds && ds.entities) || viewer.entities;

      if (!record) {
        const overlay = collection.add({
          polyline: {
            positions: elevatedPositions, // elevated positions
            width: new Cesium.ConstantProperty(width),
            material: new Cesium.ColorMaterialProperty(color),
            clampToGround: false, // do not clamp, use height
          },
        });
        overlayEntities.set(entity, { overlay, collection });
      } else if (record.overlay && record.overlay.polyline) {
        record.overlay.polyline.positions = elevatedPositions;
        record.overlay.polyline.width = new Cesium.ConstantProperty(width);
        record.overlay.polyline.material = new Cesium.ColorMaterialProperty(color);
        record.overlay.polyline.clampToGround = false;
      }

      return;
    }

    // billboard: keep old behavior
    if (entity.billboard && "color" in entity.billboard) {
      if (!changedEntities.has(entity)) {
        const oldBillboardColor = entity.billboard.color || null;
        changedEntities.set(entity, { oldBillboardColor });
      }
      try {
        entity.billboard.color = new Cesium.ConstantProperty(color);
      } catch {
        entity.billboard.color = color;
      }
    }
  }

  function setEntityGreen(entity) {
    setEntityColor(entity, Cesium.Color.GREEN, 4);
  }
  function setEntityRed(entity) {
    setEntityColor(entity, Cesium.Color.RED, 6);
  }
  function setEntityBlue(entity) {
    setEntityColor(entity, Cesium.Color.BLUE, 5);
  }
  function setEntityPurple(entity) {
    setEntityColor(entity, Cesium.Color.PURPLE, 5);
  }

  function restoreAllColors() {
    // remove overlay polylines
    overlayEntities.forEach(({ overlay, collection }) => {
      try {
        if (collection && typeof collection.remove === "function") {
          collection.remove(overlay);
        }
      } catch (e) {}
    });
    overlayEntities.clear();

    // restore billboard colors
    changedEntities.forEach((meta, entity) => {
      try {
        if (entity?.billboard) {
          if (meta.oldBillboardColor) {
            entity.billboard.color = meta.oldBillboardColor;
          } else {
            try {
              delete entity.billboard.color;
            } catch (e) {}
          }
        }
      } catch (e) {}
    });
    changedEntities.clear();
  }

  // ===== Direction & branching logic =====

  function getForwardDir(entity) {
    const info = entityCache.get(entity);
    if (info && info.forwardDir) return info.forwardDir;
    const pos = getPositionsArray(entity);
    if (!pos || pos.length < 2) return null;
    let i2 = pos.length - 1;
    let i1 = i2 - 1;
    while (i1 >= 0) {
      const v = Cesium.Cartesian3.subtract(pos[i2], pos[i1], new Cesium.Cartesian3());
      if (Cesium.Cartesian3.magnitude(v) > 0) return Cesium.Cartesian3.normalize(v, new Cesium.Cartesian3());
      i2 = i1;
      i1 = i2 - 1;
    }
    return null;
  }

  function getStartDir(entity) {
    const info = entityCache.get(entity);
    if (info && info.startDir) return info.startDir;
    const pos = getPositionsArray(entity);
    if (!pos || pos.length < 2) return null;
    let i1 = 0;
    let i2 = 1;
    while (i2 < pos.length) {
      const v = Cesium.Cartesian3.subtract(pos[i2], pos[i1], new Cesium.Cartesian3());
      if (Cesium.Cartesian3.magnitude(v) > 0) return Cesium.Cartesian3.normalize(v, new Cesium.Cartesian3());
      i1 = i2;
      i2 = i1 + 1;
    }
    return null;
  }

  function findBestNextByAngle(sourceEntity, lastPoint, toleranceMeters = MAIN_JOIN_TOLERANCE_METERS) {
    if (destroyed || !sourceEntity || !lastPoint) return null;

    const sourceDataSource = ensureEntityCacheFor(sourceEntity);
    if (!sourceDataSource) return null;

    const forwardDir = getForwardDir(sourceEntity);
    if (!forwardDir) return null;

    const entitiesArr = sourceDataSource.entities.values || sourceDataSource.entities._entities || [];
    const candidates = [];
    for (let i = 0; i < entitiesArr.length; i++) {
      const e = entitiesArr[i];
      if (!e || e === sourceEntity) continue;
      const info = entityCache.get(e);
      const first = info ? info.start : (getPositionsArray(e) || [])[0];
      if (!first) continue;
      if (cartesianNear2DMeters(first, lastPoint, toleranceMeters)) candidates.push(e);
    }
    if (candidates.length === 0) return null;
    if (candidates.length === 1) return candidates[0];

    let best = null;
    let bestAngle = Number.POSITIVE_INFINITY;
    for (const cand of candidates) {
      const startDir = getStartDir(cand);
      if (!startDir) continue;
      const angle = Cesium.Cartesian3.angleBetween(forwardDir, startDir);
      if (angle < bestAngle) {
        bestAngle = angle;
        best = cand;
      }
    }
    return best;
  }

  // ===== Side lines highlighting =====

  function getEntityPropertiesId(entity) {
    try {
      if (entity?.properties?.id !== undefined) {
        const v = resolveProp(entity.properties.id);
        if (v != null) return String(v);
      }
    } catch (e) {}
    return undefined;
  }

  function buildPropertiesIdIndexOnce() {
    if (propertiesIdIndex) return;
    propertiesIdIndex = new Map();
    if (!viewer?.dataSources || typeof viewer.dataSources.get !== "function") return;

    for (let i = 0; i < viewer.dataSources.length; i++) {
      const ds = viewer.dataSources.get(i);
      if (!ds?.entities) continue;
      const arr = ds.entities.values || ds.entities._entities || [];
      for (let j = 0; j < arr.length; j++) {
        const e = arr[j];
        if (!e) continue;
        const pid = getEntityPropertiesId(e);
        if (pid && !propertiesIdIndex.has(pid)) {
          propertiesIdIndex.set(pid, e);
        }
      }
    }
  }

  function findEntityByPropertiesId(id) {
    if (id == null || id === "") return null;
    const target = String(id);
    buildPropertiesIdIndexOnce();
    if (!propertiesIdIndex) return null;
    return propertiesIdIndex.get(target) || null;
  }

  function highlightSideLines(entity) {
    if (!entity || !entity.properties) return;

    let rightId;
    let leftId;
    try {
      rightId = resolveProp(entity.properties.right_line_id);
    } catch (e) {}
    try {
      leftId = resolveProp(entity.properties.left_line_id);
    } catch (e) {}

    if (rightId != null && rightId !== "") {
      const rightEntity = findEntityByPropertiesId(rightId);
      if (rightEntity) setEntityPurple(rightEntity);
    }
    if (leftId != null && leftId !== "") {
      const leftEntity = findEntityByPropertiesId(leftId);
      if (leftEntity) setEntityBlue(leftEntity);
    }
  }

  function areSideLinesConnected(currentLanelet, nextLanelet, sideKey) {
    if (!currentLanelet || !nextLanelet) return false;
    if (!currentLanelet.properties || !nextLanelet.properties) return false;

    const propName = sideKey === "right" ? "right_line_id" : "left_line_id";

    const curId = resolveProp(currentLanelet.properties[propName]);
    const nxtId = resolveProp(nextLanelet.properties[propName]);

    if (!curId || !nxtId) return false;

    const curSideEntity = findEntityByPropertiesId(curId);
    const nxtSideEntity = findEntityByPropertiesId(nxtId);
    if (!curSideEntity || !nxtSideEntity) return false;

    const curEnds = getLineEndpoints(curSideEntity);
    const nxtEnds = getLineEndpoints(nxtSideEntity);
    if (!curEnds || !nxtEnds) return false;

    const s1 = curEnds.start;
    const e1 = curEnds.end;
    const s2 = nxtEnds.start;
    const e2 = nxtEnds.end;

    if (cartesianNear2DMeters(s1, s2, SIDE_JOIN_TOLERANCE_METERS)) return true;
    if (cartesianNear2DMeters(s1, e2, SIDE_JOIN_TOLERANCE_METERS)) return true;
    if (cartesianNear2DMeters(e1, s2, SIDE_JOIN_TOLERANCE_METERS)) return true;
    if (cartesianNear2DMeters(e1, e2, SIDE_JOIN_TOLERANCE_METERS)) return true;

    return false;
  }

  function areAllThreeConnected(currentLanelet, nextLanelet) {
    const rightOK = areSideLinesConnected(currentLanelet, nextLanelet, "right");
    const leftOK = areSideLinesConnected(currentLanelet, nextLanelet, "left");
    return rightOK && leftOK;
  }

  // ===== Camera recentre =====

  function centerOnLastPoint(entity) {
    const pos = getPositionsArray(entity);
    if (!pos?.length || !viewer?.camera) return;
    const last = pos[pos.length - 1];

    const camera = viewer.camera;
    const carto = Cesium.Cartographic.fromCartesian(last);

    const currentHeight = camera.positionCartographic.height;
    const heading = camera.heading;
    const pitch = camera.pitch;
    const roll = camera.roll;

    camera.setView({
      destination: Cesium.Cartesian3.fromRadians(carto.longitude, carto.latitude, currentHeight),
      orientation: { heading, pitch, roll },
    });
  }

  // ===== Polling =====

  function pollingStep() {
    if (destroyed) return;
    if (!currentEntity) {
      stopAutomatic();
      return;
    }

    const positions = getPositionsArray(currentEntity);
    if (!positions || positions.length === 0) {
      stopAutomatic();
      return;
    }

    setEntityGreen(currentEntity);
    highlightSideLines(currentEntity);
    centerOnLastPoint(currentEntity);

    const lastPoint = positions[positions.length - 1];

    const next = findBestNextByAngle(currentEntity, lastPoint);
    if (next) {
      const allConnected = areAllThreeConnected(currentEntity, next);
      if (!allConnected) {
        setEntityRed(currentEntity);
        highlightSideLines(currentEntity);
        centerOnLastPoint(currentEntity);
        stopAutomatic();
        return;
      }

      currentEntity = next;
      setEntityGreen(currentEntity);
      highlightSideLines(currentEntity);
      centerOnLastPoint(currentEntity);
    } else {
      setEntityRed(currentEntity);
      highlightSideLines(currentEntity);
      centerOnLastPoint(currentEntity);
      stopAutomatic();
    }
  }

  // ===== Control =====

  function scheduleNext() {
    const ms = Math.max(0, Number(typeof getInterval === "function" ? getInterval() : (intervalMs ?? 100)));
    timerId = setTimeout(stepWrapper, ms);
  }

  function stepWrapper() {
    if (destroyed || !isRunning) return;
    pollingStep();
    if (!destroyed && isRunning) scheduleNext();
  }

  function startAutomatic() {
    if (destroyed || isRunning) return;
    isRunning = true;
    callStartOnce();
    emitState();
    scheduleNext();

    mapClickDestroyHandler.setInputAction((click) => {
      if (destroyed) return;
      if (startClickPosition && Cesium.Cartesian2.equals(click.position, startClickPosition)) {
        startClickPosition = null;
        return;
      }
      destroy();
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
  }

  function stopAutomatic() {
    if (timerId !== null) {
      clearTimeout(timerId);
      timerId = null;
    }
    isRunning = false;
    emitState();
    callCompleteOnce();
  }

  function pauseAutomatic() {
    if (!isRunning || destroyed) return;
    if (timerId !== null) {
      clearTimeout(timerId);
      timerId = null;
    }
    isRunning = false;
    emitState();
  }

  function resumeAutomatic() {
    if (destroyed || isRunning || !started || completed) return;
    isRunning = true;
    emitState();
    scheduleNext();
  }

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    try {
      pauseAutomatic();
      try {
        handler?.destroy();
      } catch {}
      try {
        mapClickDestroyHandler?.destroy();
      } catch {}
    } catch {}

    // reset caches on destroy
    entityCache.clear(); // clear entity cache
    cachedDataSource = null; // forget last dataSource
    propertiesIdIndex = null; // rebuild id index next time

    restoreAllColors();
    callCompleteOnce();
    emitState();
  }

  // ===== Boot: first click =====

  handler.setInputAction((click) => {
    if (destroyed) return;
    const picked = viewer.scene.pick(click.position);
    if (!picked) return;
    const entity = picked.id || (picked.primitive && picked.primitive.id);
    if (!entity) return;

    const layerName = getLayerName(entity, viewer);
    if (!layerName || typeof layerName !== "string") return;
    if (!layerName.toLowerCase().includes("lanelet")) return;

    currentEntity = entity;

    // build caches based on the chosen lanelet dataSource
    ensureEntityCacheFor(currentEntity);
    buildPropertiesIdIndexOnce();

    setEntityGreen(currentEntity);
    highlightSideLines(currentEntity);

    startClickPosition = click.position;
    startAutomatic();

    try {
      handler.destroy();
    } catch {}
  }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

  return {
    destroy,
    pause: pauseAutomatic,
    resume: resumeAutomatic,
    isRunning: () => isRunning,
    hasStarted: () => started,
  };
}
